import type { Table } from "@tanstack/react-table";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { columnName } from "./column-name";

/**
 * Faol filtrlar — chip bo'lib jadval tepasida. Sarlavhadagi belgi kichik:
 * ustun yashirilgan bo'lsa filtr umuman ko'rinmay qolardi, bu yerda esa
 * hammasi bir qatorda va har birini bir bosishda olib tashlash mumkin.
 */
export function ActiveFilters<TData>({ table }: { table: Table<TData> }) {
  const { t } = useTranslation();
  const filters = table.getState().columnFilters;

  const describe = (id: string, value: unknown) => {
    const column = table.getColumn(id);
    const raw = String(value);
    const option = column?.columnDef.meta?.filterOptions?.find(item => item.value === raw);
    return { name: column ? columnName(column) : id, value: option?.label ?? raw };
  };

  return (
    <AnimatePresence initial={false}>
      {filters.length > 0 ? (
        <motion.div
          key="filters"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="flex flex-wrap items-center gap-2 overflow-hidden"
        >
          <AnimatePresence initial={false}>
            {filters.map(filter => {
              const { name, value } = describe(filter.id, filter.value);
              return (
                <motion.span
                  key={filter.id}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="bg-primary/10 text-primary inline-flex h-7 items-center gap-1.5 rounded-full pr-1 pl-3 text-xs"
                >
                  <span className="text-primary/70">{name}:</span>
                  <span className="max-w-40 truncate font-medium">{value}</span>
                  <button
                    type="button"
                    aria-label={t("table.removeFilter", { column: name })}
                    onClick={() => table.getColumn(filter.id)?.setFilterValue(undefined)}
                    className="hover:bg-primary/15 grid size-5 place-items-center rounded-full"
                  >
                    <X className="size-3" />
                  </button>
                </motion.span>
              );
            })}
          </AnimatePresence>
          {filters.length > 1 ? (
            <button
              type="button"
              onClick={() => table.resetColumnFilters(true)}
              className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline"
            >
              {t("table.clearAll")}
            </button>
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
